// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Hook for checking inventory for a specific bulk item across all locations.
 * Returns a nested map structure for efficient lookups: locationId -> itemId -> quantity
 */
import { getBulkItemsInventory } from '@/inventory/api/get-bulk-items-inventory';
import { buildLocationInventoryMap } from '@/inventory/utilities/build-location-inventory-map';
import { normalizeFilters } from '@/inventory/utilities/serialize-filters';
import { THIRTY_SECONDS_MS } from '@/utilities/time-constants';
import { useQuery } from '@tanstack/react-query';

interface UseInventoryCheckOptions {
  /**
   * Include non-inventory locations in the results.
   * Useful when adjusting inventory at locations that aren't designated as inventory locations.
   */
  includeNonInventory?: boolean;
}

/**
 * Hook for checking inventory for a specific bulk item across all locations.
 * Returns a nested map structure for efficient lookups: locationId -> itemId -> quantity
 *
 * @param itemKey - Required item key to check inventory for
 * @param options - Optional configuration for the inventory check
 * @returns Object containing inventory map, loading state, and fetching state
 */
export function useInventoryCheck(
  itemKey: string,
  options?: UseInventoryCheckOptions
) {
  const filters = normalizeFilters({
    itemKeys: [itemKey],
    includeNonInventory: options?.includeNonInventory,
  });

  const result = useQuery({
    queryKey: [
      'inventory-check',
      itemKey,
      options?.includeNonInventory ?? false,
    ],
    queryFn: async () => {
      const response = await getBulkItemsInventory(filters);

      return buildLocationInventoryMap(response.data.results);
    },
    enabled: !!itemKey,
    staleTime: 0, // Always stale - refetch immediately on mount/access
    refetchInterval: THIRTY_SECONDS_MS,
    refetchOnWindowFocus: true,
  });

  return {
    inventoryMap: result.data ?? new Map(),
    isLoading: result.isLoading,
    isFetching: result.isFetching,
  };
}
